import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import Navbar from "@/components/landing/Navbar";
import Hero from "@/components/landing/Hero";
import MetricsBar from "@/components/landing/MetricsBar";
import Features from "@/components/landing/Features";
import Playground from "@/components/landing/Playground";
import StudentNotebook from "@/components/landing/StudentNotebook";
import PublicReporting from "@/components/landing/PublicReporting";
import Waitlist from "@/components/landing/Waitlist";
import Footer from "@/components/landing/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const sessionId = location.hash.split("session_id=")[1]?.split("&")[0];
    const exchange = async () => {
      try {
        const res = await axios.post(
          `${API}/auth/session`,
          { session_id: sessionId },
          { withCredentials: true }
        );
        navigate("/", { replace: true, state: { user: res.data } });
      } catch (e) {
        console.error("Auth exchange failed", e);
        navigate("/", { replace: true });
      }
    };
    exchange();
  }, [location, navigate]);

  return null;
};

const Landing = () => {
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const minDelay = new Promise((r) => setTimeout(r, 1000));
    let authCheck;
    if (location.state?.user || window.location.hash?.includes("session_id=")) {
      // CRITICAL: AuthCallback handles the exchange when returning from OAuth
      authCheck = Promise.resolve();
    } else {
      authCheck = axios
        .get(`${API}/auth/me`, { withCredentials: true })
        .then((r) => setUser(r.data))
        .catch(() => {});
    }
    Promise.all([minDelay, authCheck]).finally(() => setChecking(false));
  }, [location.state]);

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (e) {}
    setUser(null);
  };

  return (
    <div className="min-h-screen bg-[#090D16] text-slate-50 overflow-x-clip">
      <AnimatePresence>
        {checking && (
          <motion.div
            data-testid="app-loading-screen"
            exit={{ opacity: 0 }}
            transition={{ duration: 0.45 }}
            className="fixed inset-0 z-[100] bg-[#090D16] flex flex-col items-center justify-center"
          >
            <motion.img
              src="/logo.png"
              alt="InNewZ"
              initial={{ scale: 0.82, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              className="h-20 w-20"
            />
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15, duration: 0.4 }}
              className="font-display font-bold text-2xl tracking-tight mt-5"
            >
              InNew<span className="text-cyan-400">Z</span>
            </motion.p>
            <motion.div
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 0.85, ease: "easeInOut" }}
              className="mt-6 h-0.5 w-28 origin-left rounded-full bg-gradient-to-r from-cyan-400 to-blue-600"
            />
          </motion.div>
        )}
      </AnimatePresence>
      <div className="noise-overlay" />
      <Navbar user={user} onLogout={logout} />
      <main>
        <Hero />
        <MetricsBar />
        <Features />
        <Playground />
        <StudentNotebook />
        <PublicReporting />
        <Waitlist user={user} />
      </main>
      <Footer />
    </div>
  );
};

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
      <Toaster theme="dark" position="top-center" richColors />
    </div>
  );
}

export default App;
